import type { PenaltyItemDto } from '@customTypes/types';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import {
  useClubProfilePenaltiesMutation,
  useClubProfilePenaltiesQuery
} from '@/shared/api/requests/club';

export const usePenaltiesPage = () => {
  const { formatMessage } = useIntl();
  const { id: clubId } = useParams({ from: '/clubOwner/$id' });
  const navigate = useNavigate({ from: '/clubOwner/$id/onboard/$step' });
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const penalties = useClubProfilePenaltiesQuery({ clubId });
  const penaltiesMutation = useClubProfilePenaltiesMutation();

  const queries = [penalties];
  const isLoading = queries.some((q) => q.isLoading);

  const form = useForm<{
    penalties: Omit<PenaltyItemDto, 'penaltyItemId'>[];
  }>({
    initialValues: {
      penalties: []
    }
  });

  useEffect(() => {
    if (penalties.isSuccess && penalties.data.data.length) {
      form.initialize({ penalties: penalties.data.data });
    }
  }, [penalties.data?.data]);

  const isFormValid = form.getValues().penalties.length;

  const next = () => {
    navigate({
      to: '/clubOwner/$id/onboard/$step',
      params: {
        step: 'photo'
      }
    });
  };

  const onSubmit = form.onSubmit(async (values) => {
    if (isFormValid) {
      await penaltiesMutation.mutateAsync(
        {
          params: {
            data: values.penalties,
            clubId
          }
        },
        {
          onSuccess: async () => {
            notifications.show({
              title: formatMessage({ id: 'notifications.successDataUpdate' }),
              ...notificationStyleConfig
            });
            await queryClient.invalidateQueries({
              queryKey: ['ClubProfilePenalties', clubId]
            });
            next();
          }
        }
      );
    } else {
      next();
    }
  });

  return {
    state: { isLoading, form, inputValue, isFormValid },
    functions: { onSubmit, setInputValue, next }
  };
};
